import { useInfiniteQuery } from '@tanstack/react-query'
import {
  socialUserFollowersQueryKey,
  socialUserFollowersQueryRootKey,
  socialUserFollowersQueryFetcher,
  socialUserFollowingQueryKey,
  socialUserFollowingQueryRootKey,
  socialUserFollowingQueryFetcher,
} from '@web/gen/crafter'

interface Pagination {
  totalPages: number
  itemsPerPage: number
  currentPage: number
}

export function useSocialUserFollowersInfiniteQuery(
  userId: string,
  options?: { enabled?: boolean },
) {
  const variables = { input: { userId, itemsPerPage: 20 } }

  return useInfiniteQuery({
    queryKey: [socialUserFollowersQueryRootKey, ...socialUserFollowersQueryKey(variables), userId],
    queryFn: ({ pageParam }) =>
      socialUserFollowersQueryFetcher({
        input: { userId, itemsPerPage: 20, page: pageParam ?? 1 },
      })(),

    enabled: Boolean(userId) && (options?.enabled ?? true),
    initialPageParam: 1,
    getNextPageParam: lastPage => {
      if (!lastPage) {
        return 1
      }

      const pagination = lastPage?.userFollowers?.pagination as Pagination | undefined
      if (!pagination) {
        return
      }

      const next = (pagination.currentPage ?? 1) + 1

      return next <= (pagination.totalPages ?? 1) ? next : undefined
    },
    refetchOnWindowFocus: false,
    staleTime: 60_000,
  })
}

export function useSocialUserFollowingInfiniteQuery(
  userId: string,
  options?: { enabled?: boolean },
) {
  const variables = { input: { userId, itemsPerPage: 20 } }

  return useInfiniteQuery({
    queryKey: [socialUserFollowingQueryRootKey, ...socialUserFollowingQueryKey(variables), userId],
    queryFn: ({ pageParam }) =>
      socialUserFollowingQueryFetcher({
        input: { userId, itemsPerPage: 20, page: pageParam ?? 1 },
      })(),
    enabled: Boolean(userId) && (options?.enabled ?? true),
    initialPageParam: 1,
    getNextPageParam: lastPage => {
      if (!lastPage) {
        return 1
      }

      const pagination = lastPage?.userFollowing?.pagination as Pagination | undefined
      if (!pagination) {
        return
      }

      const next = (pagination.currentPage ?? 1) + 1

      return next <= (pagination.totalPages ?? 1) ? next : undefined
    },

    refetchOnWindowFocus: false,
    staleTime: 60_000,
  })
}
